import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { LineChart as LineIcon, Info } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { C, fontSerif, fontMono, fmtDateShort } from './utils';

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border bg-white/95 px-3 py-2 text-xs shadow-lg backdrop-blur-md" style={{ borderColor: C.border }}>
      <div className="font-semibold" style={{ color: C.text }}>{fmtDateShort(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="mt-1 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}:</span>
          <span className="font-bold tabular-nums" style={{ fontFamily: fontMono, color: C.text }}>
            {p.value == null ? '—' : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export const DailyHistoryView = ({ history, threshold = 85, employees, selectedId, onSelect, canPick }) => {
  const data = (history || []).map((s) => ({
    date: s.date,
    score: s.overall_score,
    projected: s.projected_month_end,
    adherence: s.daily_adherence_pct == null ? null : Math.round(s.daily_adherence_pct * 10) / 10,
  }));

  return (
    <div className="space-y-6" data-testid="daily-history-view">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold" style={{ fontFamily: fontSerif, color: C.text }}>Daily Trajectory</h2>
          <p className="text-sm text-slate-500">How the score has moved on each closed day this period.</p>
        </div>
        {canPick && employees?.length > 0 && (
          <Select value={selectedId || ''} onValueChange={onSelect}>
            <SelectTrigger className="w-56 bg-white" data-testid="history-employee-select">
              <SelectValue placeholder="Select teammate" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id} data-testid={`history-emp-${e.id}`}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="rounded-2xl border bg-white p-6" style={{ borderColor: C.border }}>
        {data.length === 0 ? (
          <div className="flex h-72 flex-col items-center justify-center text-center">
            <LineIcon className="h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm font-medium text-slate-500">No closed days yet</p>
            <p className="text-xs text-slate-400">The chart fills in as the admin closes each working day.</p>
          </div>
        ) : (
          <div style={{ width: '100%', height: 340 }} data-testid="score-trajectory-chart">
            <ResponsiveContainer>
              <AreaChart data={data} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.burgundy} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={C.burgundy} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDateShort} tick={{ fontSize: 11, fill: C.text3 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 110]} tick={{ fontSize: 11, fill: C.text3 }} axisLine={false} tickLine={false} />
                <Tooltip content={<TooltipBox />} />
                <ReferenceLine y={threshold} stroke={C.gold} strokeDasharray="5 4"
                  label={{ value: `Reward ${threshold}`, position: 'right', fill: C.gold, fontSize: 10 }} />
                <Area type="monotone" dataKey="score" name="Score" stroke={C.burgundy} strokeWidth={2.5} fill="url(#scoreFill)" connectNulls dot={{ r: 3, fill: C.burgundy }} activeDot={{ r: 5 }} />
                <Area type="monotone" dataKey="adherence" name="Daily adherence" stroke={C.indigo} strokeWidth={2} fill="none" connectNulls dot={false} strokeDasharray="4 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: C.burgundy }} /> Overall score</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: C.indigo }} /> Daily adherence %</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: C.gold }} /> Reward line</span>
          <span className="inline-flex items-center gap-1 text-slate-400"><Info className="h-3.5 w-3.5" /> Snapshots are taken when a day is closed</span>
        </div>
      </div>
    </div>
  );
};
